import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchClinicProfile, updateClinicProfile } from "../../features/clinics/clinicProfileSlice";
import { Paper, Typography, TextField, Button, Grid, Box, CircularProgress } from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Clinic = () => {
  const dispatch = useDispatch();
  const { data: clinicData, loading } = useSelector((state) => state.clinic);
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  // Fetch clinic data on mount
  useEffect(() => {
    dispatch(fetchClinicProfile());
  }, [dispatch]);

  // Sync state when API data is available
  useEffect(() => {
    if (clinicData) {
      setFormData(clinicData);
    }
  }, [clinicData]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle save/update
  const handleSave = () => {
    dispatch(updateClinicProfile(formData));
    setIsEditing(false);
  };

  if (loading) return <CircularProgress sx={{ display: "block", margin: "auto" }} />;
  if (!clinicData) return <Typography>Loading clinic data...</Typography>;

  return (
    <Box sx={{ padding: 3 }}>
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Clinic Details
      </Typography>
      <Paper sx={{ padding: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Clinic Name"
              fullWidth
              name="clinic_name"
              value={formData.clinic_name || ""}
              onChange={handleInputChange}
              InputProps={{ readOnly: !isEditing }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Address"
              fullWidth
              name="address"
              value={formData.address || ""}
              onChange={handleInputChange}
              InputProps={{ readOnly: !isEditing }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Phone"
              fullWidth
              name="phone_number"
              value={formData.phone_number || ""}
              onChange={handleInputChange}
              InputProps={{ readOnly: !isEditing }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              fullWidth
              name="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              InputProps={{ readOnly: !isEditing }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Working Hours"
              fullWidth
              name="working_hours"
              value={formData.working_hours || ""}
              onChange={handleInputChange}
              InputProps={{ readOnly: !isEditing }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Services Offered"
              fullWidth
              name="services_offered"
              value={formData.services_offered || ""}
              onChange={handleInputChange}
              InputProps={{ readOnly: !isEditing }}
            />
          </Grid>
          <Grid item xs={12}>
            {isEditing ? (
              <Button variant="contained" color="primary" onClick={handleSave} sx={{ marginTop: 2 }}>
                Save Changes
              </Button>
            ) : (
              <Button variant="contained" color="primary" onClick={() => setIsEditing(true)} sx={{ marginTop: 2 }}>
                Edit Clinic
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h5" gutterBottom>
          Preview
        </Typography>
        <Paper sx={{ padding: 2 }}>
          <Typography variant="h6">{formData.clinic_name}</Typography>
          <Typography variant="body1">Address: {formData.address}</Typography>
          <Typography variant="body1">Phone: {formData.phone_number}</Typography>
          <Typography variant="body1">Email: {formData.email}</Typography>
          <Typography variant="body1">Working Hours: {formData.working_hours}</Typography>
          <Typography variant="body1">Services: {formData.services_offered}</Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default Clinic;
